import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchJson } from "@/lib/queryClient";
import { filtrarVariedadesDoCacho } from "@shared/culturas";
import type { VariedadeWithCultura } from "@shared/schema";

/**
 * Variedades como o servidor devolve — TODAS as culturas.
 *
 * Só para LOOKUP (achar o nome da variedade de um setor). Filtrar aqui faria o
 * rótulo de um setor de citros virar "—" nas telas que listam a fazenda inteira.
 * Para SELETOR, use `useVariedadesDoCacho`.
 *
 * `queryKey` é a mesma dos consumidores antigos de propósito: cache
 * compartilhado, e o `invalidateQueries` da tela de Variedades continua valendo.
 * `fetchJson` porque queryFn que não checa `r.ok` vira white-screen (armadilha
 * conhecida do repo).
 */
export function useVariedades() {
  return useQuery<VariedadeWithCultura[]>({
    queryKey: ["/api/variedades"],
    queryFn: () => fetchJson<VariedadeWithCultura[]>("/api/variedades"),
  });
}

/**
 * Variedades do módulo **Cacho** — só banana (`CACHO_CULTURAS`).
 *
 * Existe porque o filtro "Variedade" do Controle de Fitas listava **Pêra Rio**,
 * que é citros: não há mangará, fita nem ciclo de laranja, então a opção só
 * podia devolver tela vazia.
 *
 * **Fail-open**: variedade sem cultura resolvida continua na lista (ver
 * `variedadeDoCacho`). O gating esconde o que é comprovadamente de outra
 * lavoura, nunca o que ele não conseguiu classificar.
 *
 * NÃO usar em `variedades.tsx` (Culturas e Variedades é admin e mostra tudo) nem
 * em `setores.tsx` (o mapeamento setor → variedade precisa de todas as culturas).
 */
export function useVariedadesDoCacho() {
  const { data, ...rest } = useVariedades();

  const variedades = useMemo(() => {
    if (!data) return data;
    return filtrarVariedadesDoCacho(data);
  }, [data]);

  return { variedades, ...rest };
}
